/**
 * Hash router maison Nestor (cf. ADR 0004).
 *
 * Schéma d'URL :
 *   #/                    → home (mode classique)
 *   #/messages | #/planning | #/photos | #/meals | #/settings
 *   #/cp/                 → home (mode co-parentage)
 *   #/cp/activities | #/cp/liaison | #/cp/frais | #/cp/settings
 *   #/cp/bag | #/cp/school   (routes secondaires, pas dans le bottom nav)
 *   #/onboarding | #/login
 *
 * Règles :
 *   - Le préfixe `#/cp/` signale le mode co-parentage et est réservé.
 *   - Toute route inconnue retombe sur `/` ou `/cp/` selon le mode courant
 *     (sera précisé en incrément 3 quand le store homes sera là — pour
 *     l'instant fallback sur `/`).
 *   - `currentHomeId` n'est jamais dans l'URL — c'est un état utilisateur.
 *
 * API publique :
 *   initRouter(handler)   → s'abonne aux changements et délivre la route
 *   navigate(route, opts) → change l'URL (et déclenche l'handler)
 *   currentRoute()        → lit la route courante depuis l'URL
 *   parseRoute(hash)      → utilitaire pur (testable sans DOM)
 *   modeOf(route)         → 'classic' | 'coparenting' (dérivé de la route)
 */

export type ClassicRoute = '/' | '/messages' | '/planning' | '/photos' | '/meals' | '/settings';

export type CoparentingRoute =
  | '/cp/'
  | '/cp/activities'
  | '/cp/liaison'
  | '/cp/frais'
  | '/cp/settings'
  | '/cp/bag'
  | '/cp/school';

export type AuthRoute = '/onboarding' | '/login' | '/invite';

export type Route = ClassicRoute | CoparentingRoute | AuthRoute;

export const CLASSIC_ROUTES = [
  '/',
  '/messages',
  '/planning',
  '/photos',
  '/meals',
  '/settings',
] as const satisfies readonly ClassicRoute[];

export const COPARENTING_ROUTES = [
  '/cp/',
  '/cp/activities',
  '/cp/liaison',
  '/cp/frais',
  '/cp/settings',
  '/cp/bag',
  '/cp/school',
] as const satisfies readonly CoparentingRoute[];

export const AUTH_ROUTES = [
  '/onboarding',
  '/login',
  '/invite',
] as const satisfies readonly AuthRoute[];

const ALL_ROUTES = new Set<Route>([...CLASSIC_ROUTES, ...COPARENTING_ROUTES, ...AUTH_ROUTES]);

export type AppMode = 'classic' | 'coparenting';

/** Déduit le mode depuis la route (auth → mode courant inconnu, défaut classic). */
export function modeOf(route: Route): AppMode {
  return route.startsWith('/cp/') || route === '/cp/' ? 'coparenting' : 'classic';
}

/**
 * Parse un hash brut (`#/cp/liaison`, `/cp/liaison`, `cp/liaison`, `''`)
 * en route normalisée. Toute route inconnue → fallback `/`.
 *
 * Pure : ne lit pas `window`. Testable sans DOM.
 */
export function parseRoute(hash: string): Route {
  let raw = hash;
  if (raw.startsWith('#')) raw = raw.slice(1);
  if (raw === '' || raw === '/') return '/';
  if (!raw.startsWith('/')) raw = `/${raw}`;
  // Normalisation : enlève la query string et le fragment éventuel
  raw = raw.split('?')[0]!.split('#')[0]!;
  // Normalisation co-parentage : `/cp` → `/cp/`
  if (raw === '/cp') raw = '/cp/';

  // Phase 6.2 : route paramétrée `/invite/<token>` → on normalise
  // sur le sentinel `/invite`. Le token est extrait séparément via
  // `parseInviteToken()` (côté View) car le système Route est
  // basé sur des string literals figés. **Exige un token non-vide
  // après `/invite/`** : `/invite` seul ou `/invite/` retombent
  // sur `/` (URL malformée).
  if (raw.startsWith('/invite/') && raw.length > '/invite/'.length) return '/invite';

  // `/invite` (sans token) est considéré comme une URL malformée
  // — on ne le matche PAS comme route. ALL_ROUTES contient
  // `/invite` pour la complétude du type, mais on filtre ici.
  if (raw === '/invite') return '/';

  if (ALL_ROUTES.has(raw as Route)) return raw as Route;
  return '/';
}

/**
 * Extrait le token d'une URL `/invite/<token>` — Phase 6.2.
 *
 * Lit `window.location.hash` (ou le param `hash` fourni en test) et
 * retourne le token brut (non décodé URL). Retourne `null` si l'URL
 * ne matche pas le pattern.
 *
 * Le View `modules/invite/View.ts` appelle cette fonction au render
 * pour récupérer le token associé à la route `/invite` courante.
 */
export function parseInviteToken(hash?: string): string | null {
  const raw = (hash ?? readHash()).startsWith('#')
    ? (hash ?? readHash()).slice(1)
    : (hash ?? readHash());
  const stripped = raw.split('?')[0]!.split('#')[0]!;
  if (!stripped.startsWith('/invite/')) return null;
  const token = stripped.slice('/invite/'.length);
  if (!token) return null;
  return token;
}

export type RouteListener = (route: Route) => void;

let currentListener: RouteListener | null = null;
let hashChangeBound = false;
let lastNotified: Route | null = null;

function getWindow(): (Window & typeof globalThis) | undefined {
  return typeof window === 'undefined' ? undefined : window;
}

function readHash(): string {
  const w = getWindow();
  return w?.location.hash ?? '';
}

function writeHash(route: Route, replace: boolean): void {
  const w = getWindow();
  if (!w) return;
  const newHash = `#${route}`;
  if (w.location.hash === newHash) return;
  if (replace) {
    const url = `${w.location.pathname}${w.location.search}${newHash}`;
    w.history.replaceState(null, '', url);
  } else {
    // pushState plutôt que `location.hash = ...` — pushState est synchrone
    // et n'émet pas hashchange (qu'on notifierait manuellement après) ;
    // ça nous donne un comportement déterministe sans dépendre du timing
    // de l'event loop. Le bouton back du navigateur est géré par hashchange.
    const url = `${w.location.pathname}${w.location.search}${newHash}`;
    w.history.pushState(null, '', url);
  }
  notify();
}

function notify(): void {
  if (!currentListener) return;
  const route = currentRoute();
  if (route === lastNotified) return;
  lastNotified = route;
  currentListener(route);
}

/** Route courante lue depuis `window.location.hash`. */
export function currentRoute(): Route {
  return parseRoute(readHash());
}

/**
 * Change la route courante.
 *
 * - `replace: false` (défaut) : ajoute une entrée dans l'historique.
 * - `replace: true` : remplace l'entrée courante (pas de retour arrière).
 *
 * Déclenche le listener enregistré via `initRouter`.
 */
export function navigate(route: Route, opts: { replace?: boolean } = {}): void {
  writeHash(route, opts.replace ?? false);
}

/**
 * Initialise le router. Le listener reçoit immédiatement la route courante,
 * puis est appelé à chaque changement (`hashchange` ou `navigate`).
 *
 * Retourne une fonction de désabonnement (utile aux tests).
 */
export function initRouter(listener: RouteListener): () => void {
  currentListener = listener;
  lastNotified = null;

  const w = getWindow();
  if (w && !hashChangeBound) {
    w.addEventListener('hashchange', notify);
    hashChangeBound = true;
  }

  // Si l'URL est vide à l'arrivée, on pose un hash explicite (`#/`) pour
  // que les futurs changements soient cohérents.
  if (readHash() === '') {
    writeHash('/', true);
  } else {
    notify();
  }

  return () => {
    if (currentListener === listener) currentListener = null;
    if (w && hashChangeBound) {
      w.removeEventListener('hashchange', notify);
      hashChangeBound = false;
    }
    lastNotified = null;
  };
}
